import React, { useCallback, useEffect } from 'react';
import { WidgetWrapper } from '../../../../components/WidgetWrapper/WidgetWrapper';
import { EditField } from '../../../../components/EditPanel/types';
import { ChatInputPreset, ChatInputProps, ChatInputState } from './types';
import { useWidgetEditable } from '../../../../hooks/useWidgetEditable';
import { FONT_WEIGHT_OPTIONS_KEYWORDS } from '../../../../utils/fontWeightOptions';
import { themeColor, customColor } from '../../../../types/color';

export const CHAT_INPUT_PRESETS: Record<Exclude<ChatInputPreset, 'custom'>, Partial<ChatInputState>> = {
  expanded: {
    fontSize: 16,
    iconSize: 24,
    iconGap: 12,
    shadowEnabled: true,
    shadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.05)',
    borderColor: customColor('#DCDDDC'),
    borderColorFocus: themeColor('borderColor'),
    borderRadiusSingleLine: 28,
    borderRadiusMultiLine: 28,
    borderWidth: 1,
    paddingX: 16,
    paddingY: 16,
    minHeight: 138,
    submitButtonSize: 24,
    mobile_fontSize: 14,
    mobile_paddingX: 14,
    mobile_paddingY: 14,
    mobile_minHeight: 116,
    mobile_iconGap: 4,
    mobile_iconSize: 20,
    mobile_submitButtonSize: 20,
    mobile_borderRadiusSingleLine: 26,
    mobile_borderRadiusMultiLine: 26,
  },
  compact: {
    fontSize: 16,
    iconSize: 24,
    iconGap: 12,
    shadowEnabled: true,
    shadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.05)',
    borderColor: customColor('#DCDDDC'),
    borderColorFocus: themeColor('borderColor'),
    borderRadiusSingleLine: 28,
    borderRadiusMultiLine: 28,
    borderWidth: 1,
    paddingX: 16,
    paddingY: 16,
    minHeight: 56,
    submitButtonSize: 24,
    mobile_fontSize: 14,
    mobile_paddingX: 14,
    mobile_paddingY: 14,
    mobile_minHeight: 46,
    mobile_iconGap: 4,
    mobile_iconSize: 20,
    mobile_submitButtonSize: 20,
    mobile_borderRadiusSingleLine: 26,
    mobile_borderRadiusMultiLine: 26,
  },
};

/** Default initial state for the panel home input (concierge-panel-chat-input). */
export const PANEL_CHAT_INPUT_PRESETS: Record<Exclude<ChatInputPreset, 'custom'>, Partial<ChatInputState>> = {
  expanded: {
    fontSize: 14,
    iconSize: 20,
    iconGap: 6,
    backgroundColor: themeColor('background'),
    shadowEnabled: true,
    shadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.05)',
    borderColor: customColor('#DCDDDC'),
    borderColorFocus: customColor('#DCDDDC'),
    borderRadiusSingleLine: 20,
    borderRadiusMultiLine: 15,
    borderWidth: 1,
    paddingX: 18,
    paddingY: 18,
    minHeight: 116,
    mobile_paddingX: 16,
    mobile_paddingY: 16,
    mobile_minHeight: 116,
  },
  compact: {
    fontSize: 14,
    iconSize: 20,
    iconGap: 6,
    backgroundColor: themeColor('background'),
    shadowEnabled: true,
    shadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.05)',
    borderColor: customColor('#DCDDDC'),
    borderColorFocus: customColor('#DCDDDC'),
    borderRadiusSingleLine: 28,
    borderRadiusMultiLine: 15,
    borderWidth: 1,
    paddingX: 18,
    paddingY: 18,
    minHeight: 42,
    submitButtonSize: 24,
    mobile_fontSize: 14,
    mobile_paddingX: 16,
    mobile_paddingY: 16,
    mobile_minHeight: 42,
    mobile_iconGap: 6,
    mobile_iconSize: 20,
    mobile_submitButtonSize: 20,
  },
};

/**
 * Load a Google Font stylesheet into document.head.
 * This is necessary because fonts loaded inside Shadow DOM don't register
 * @font-face declarations globally, causing the font to not render.
 */
const loadGoogleFontInHead = (fontUrl: string): void => {
  // Check if already loaded
  const existingLink = document.querySelector(`link[href="${fontUrl}"]`);
  if (existingLink) return;

  // Inject into document.head (works with Shadow DOM)
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = fontUrl;
  document.head.appendChild(link);
};

const defaultState: ChatInputState = {
  preset: 'expanded',

  // Background
  backgroundColor: themeColor('background'),
  shadowEnabled: true,
  shadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.05)',

  // Border styling
  borderColor: customColor('#DCDDDC'),
  borderColorFocus: themeColor('borderColor'),
  borderRadiusSingleLine: 28,
  borderRadiusMultiLine: 28,
  borderWidth: 1,

  // Submit button
  submitButtonBgColor: themeColor('textPrimary'),
  submitButtonBgColorHover: themeColor('textPrimary'),
  submitButtonIconColor: themeColor('background'),
  submitButtonSize: 24,
  submitButtonBorderRadius: 12,

  // Action icons (attachment, mic)
  iconColor: themeColor('textPrimary'),
  iconColorHover: themeColor('tertiary'),
  iconSize: 24,
  iconGap: 12,
  micActiveColor: themeColor('error'),
  microphoneIcon: 'default',

  // Textarea typography
  fontSize: 16,
  fontWeight: 'medium',
  fontFamily: 'inherit',
  textColor: themeColor('textPrimary'),
  placeholderColor: themeColor('textTertiary'),

  // Layout — 24px horizontal padding, 56px min-height per design spec
  paddingX: 24,
  paddingY: 12,
  maxWidth: 768,
  minHeight: 56,

  // Visibility toggles
  showAttachmentButton: true,
  showMicrophoneButton: true,

  // Mobile overrides
  mobile_fontSize: 14,
  mobile_paddingX: 12,
  mobile_paddingY: 12,
  mobile_minHeight: 46,
  mobile_iconGap: 4,
  mobile_iconSize: 20,
  mobile_submitButtonSize: 20,
  mobile_borderRadiusSingleLine: 26,
  mobile_borderRadiusMultiLine: 26,
};

const editFields: EditField[] = [
  {
    key: 'preset',
    label: 'Preset',
    type: 'options',
    options: [
      { label: 'Expanded', value: 'expanded' },
      { label: 'Compact', value: 'compact' },
      { label: 'Custom', value: 'custom' },
    ],
  },
  // Background
  { key: 'backgroundColor', label: 'Background Color', type: 'color' },
  { key: 'shadowEnabled', label: 'Shadow', type: 'toggle' },
  { key: 'shadow', label: 'Shadow Value (CSS)', type: 'text' },
  // Border styling group
  { key: 'borderColor', label: 'Border Color', type: 'color' },
  { key: 'borderColorFocus', label: 'Border Color (Focus)', type: 'color' },
  {
    key: 'borderRadiusSingleLine',
    label: 'Border Radius (Single Line)',
    type: 'number',
    min: 0,
    max: 100,
    step: 1,
    responsive: true,
  },
  {
    key: 'borderRadiusMultiLine',
    label: 'Border Radius (Multi Line)',
    type: 'number',
    min: 0,
    max: 50,
    step: 1,
    responsive: true,
  },
  {
    key: 'borderWidth',
    label: 'Border Width',
    type: 'number',
    min: 0,
    max: 5,
    step: 1,
  },

  // Submit button group
  { key: 'submitButtonBgColor', label: 'Submit Button Color', type: 'color' },
  {
    key: 'submitButtonBgColorHover',
    label: 'Submit Button Hover Color',
    type: 'color',
  },
  {
    key: 'submitButtonIconColor',
    label: 'Submit Button Icon Color',
    type: 'color',
  },
  {
    key: 'submitButtonSize',
    label: 'Submit Button Size',
    type: 'number',
    min: 16,
    max: 48,
    step: 2,
  },
  {
    key: 'submitButtonBorderRadius',
    label: 'Submit Button Border Radius',
    type: 'number',
    min: 0,
    max: 24,
    step: 1,
  },

  // Action icons group
  { key: 'iconColor', label: 'Icon Color', type: 'color' },
  { key: 'iconColorHover', label: 'Icon Hover Color', type: 'color' },
  {
    key: 'iconSize',
    label: 'Icon Size',
    type: 'number',
    min: 16,
    max: 48,
    step: 2,
  },
  {
    key: 'iconGap',
    label: 'Icon Gap',
    type: 'number',
    min: 4,
    max: 32,
    step: 2,
  },
  { key: 'micActiveColor', label: 'Mic Active Color', type: 'color' },
  { key: 'microphoneIcon', label: 'Microphone Icon', type: 'icon' },

  // Typography group
  {
    key: 'fontSize',
    label: 'Font Size',
    type: 'number',
    min: 12,
    max: 24,
    step: 1,
    responsive: true,
  },
  {
    key: 'fontWeight',
    label: 'Font Weight',
    type: 'options',
    options: FONT_WEIGHT_OPTIONS_KEYWORDS,
  },
  {
    key: 'fontFamily',
    label: 'Font Family',
    type: 'font',
  },
  { key: 'textColor', label: 'Text Color', type: 'color' },
  { key: 'placeholderColor', label: 'Placeholder Color', type: 'color' },

  // Layout group
  {
    key: 'paddingX',
    label: 'Horizontal Padding',
    type: 'number',
    min: 0,
    max: 32,
    step: 2,
    responsive: true,
  },
  {
    key: 'paddingY',
    label: 'Vertical Padding',
    type: 'number',
    min: 0,
    max: 32,
    step: 2,
    responsive: true,
  },
  {
    key: 'maxWidth',
    label: 'Max Width',
    type: 'number',
    min: 300,
    max: 1200,
    step: 50,
  },
  {
    key: 'minHeight',
    label: 'Min Height (0=auto)',
    type: 'number',
    min: 0,
    max: 300,
    step: 10,
    responsive: true,
  },

  // Visibility toggles
  {
    key: 'showAttachmentButton',
    label: 'Show Attachment Button',
    type: 'options',
    options: [
      { label: 'Yes', value: 'true' },
      { label: 'No', value: 'false' },
    ],
  },
  {
    key: 'showMicrophoneButton',
    label: 'Show Microphone Button',
    type: 'options',
    options: [
      { label: 'Yes', value: 'true' },
      { label: 'No', value: 'false' },
    ],
  },
];

export const ChatInput: React.FC<ChatInputProps> = ({
  initialState = {},
  widgetId,
  displayName,
  presets: presetsProp,
  children,
}) => {
  const activePresets = presetsProp ?? CHAT_INPUT_PRESETS;

  const {
    state,
    isLoading,
    isSaving,
    handleEditChange: rawHandleEditChange,
    handleSave,
    resetField,
    savedKeys,
    themeColors,
    themeFonts,
    themeIcons,
    resolveColor,
    canSave,
    getEditValues,
  } = useWidgetEditable<ChatInputState>(widgetId, { ...defaultState, ...initialState }, editFields);

  const handleEditChange = useCallback(
    (key: string, value: any) => {
      if (key === 'preset' && value !== 'custom') {
        const presetKey = value as Exclude<ChatInputPreset, 'custom'>;
        const config = activePresets[presetKey];
        if (config) {
          rawHandleEditChange('preset', value);
          Object.entries(config).forEach(([k, v]) => {
            rawHandleEditChange(k, v);
          });
          return;
        }
      }
      if (key !== 'preset') {
        rawHandleEditChange('preset', 'custom');
      }
      rawHandleEditChange(key, value);
    },
    [rawHandleEditChange],
  );

  // Resolve font family - if "inherit", use inherit; otherwise look up from themeFonts
  const fontKey = state.fontFamily;
  const fontData = fontKey && fontKey !== 'inherit' ? themeFonts[fontKey] : null;

  // For Google Fonts, we need to load via <link> tag in document.head
  // (not inline, because Shadow DOM isolates @font-face declarations)
  const googleFontLink = fontData && fontData.format === 'google' ? fontData.url : null;

  // Load Google Font into document.head when font changes
  // NOTE: This hook must be called before any early returns to satisfy React's rules of hooks
  useEffect(() => {
    if (googleFontLink) {
      loadGoogleFontInHead(googleFontLink);
    }
  }, [googleFontLink]);

  // Migrate old configs: if iconGap was never saved, the config predates the
  // icon-size update. Push the new defaults so they're visible immediately.
  useEffect(() => {
    if (!isLoading && !savedKeys.has('iconGap')) {
      if (savedKeys.has('iconSize') && state.iconSize !== defaultState.iconSize) {
        handleEditChange('iconSize', defaultState.iconSize);
      }
      if (savedKeys.has('submitButtonSize') && state.submitButtonSize !== defaultState.submitButtonSize) {
        handleEditChange('submitButtonSize', defaultState.submitButtonSize);
      }
    }
  }, [isLoading]); // eslint-disable-line

  // Show loading state while fetching theme config
  if (isLoading) {
    return <></>;
  }

  // Determine font-family CSS based on font type
  // - system: use the url field which contains the CSS font-family value
  // - google/upload: use the font name
  const fontFamilyCss = fontData
    ? fontData.format === 'system'
      ? fontData.url // System fonts store CSS font-family in url field
      : `"${fontData.name}", sans-serif`
    : 'inherit';

  // Determine if we need @font-face (only for uploaded fonts with actual file URLs)
  const needsFontFace = fontData && fontData.format !== 'system' && fontData.format !== 'google';

  // Generate unique class for this widget instance (stable based on widgetId)
  const uniqueClass = `chat-input-${widgetId || 'default'}`;

  // Generate @font-face CSS only for uploaded fonts
  // Also include !important override rule for font-family
  const styleCss = `
    ${
      needsFontFace
        ? `
      @font-face {
        font-family: "${fontData.name}";
        src: url("${fontData.url}") format("${fontData.format}");
        font-display: swap;
      }
    `
        : ''
    }
    ${fontFamilyCss !== 'inherit' ? `.${uniqueClass} { font-family: ${fontFamilyCss} !important; }` : ''}
  `.trim();

  // Create enhanced state with resolved fontFamily CSS
  const enhancedState: ChatInputState & { fontFamilyCss: string } = {
    ...state,
    fontFamilyCss,
  };

  // Render the children with current state, themeIcons, and font styles
  const widgetContent = (
    <>
      {styleCss && <style>{styleCss}</style>}
      {children(enhancedState, themeIcons, resolveColor)}
    </>
  );

  // Wrap with WidgetWrapper for edit capability
  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName={displayName}
      editFields={editFields}
      editValues={getEditValues()}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      savedKeys={savedKeys}
      onResetField={resetField}
      themeColors={themeColors}
      themeFonts={themeFonts}
      themeIcons={themeIcons}
    >
      {widgetContent}
    </WidgetWrapper>
  );
};
